import React, { useState, useEffect } from 'react';
import { Users, TrendingUp, Gift, Download, Clock, Info, RefreshCw } from 'lucide-react';
// import services
import { getDashboard } from '../services/admin.service';

const Dashboard = () => {
    // state
    const [balanceInfo, setBalanceInfo] = useState({
        "total_binary_bonus": 0,
        "total_business_bonus": 0,
        "total_cash_bonus": 0,
        "total_refund_bonus": 0,
        "available_binary_bonus": 0,
        "available_business_bonus": 0,
        "available_cash_bonus": 0,
        "available_refund_bonus": 0,
        "pending_binary_bonus": 0,
        "pending_business_bonus": 0,
        "pending_cash_bonus": 0,
        "pending_refund_bonus": 0,
        "withdraw_binary_bonus": 0,
        "withdraw_business_bonus": 0,
        "withdraw_cash_bonus": 0,
        "withdraw_refund_bonus": 0
    });

    const IncomeCard = ({ title, total, available, pending, withdraw, icon: Icon, gradientFrom, gradientTo }) => (
        <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl shadow-lg p-6 border border-gray-200">
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                    <div className={`p-3 rounded-lg bg-gradient-to-br ${gradientFrom} ${gradientTo}`}>
                        <Icon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                        <h3 className="font-semibold text-gray-800">{title}</h3>
                        <p className="text-2xl md:text-3xl font-bold text-gray-900">₹{total}</p>
                    </div>
                </div>
            </div>

            <div className="space-y-3">
                {/* Available Balance */}
                <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-center gap-2">
                        <Info className="w-4 h-4 text-blue-600" />
                        <span className="text-sm font-medium text-blue-800">Available</span>
                    </div>
                    <span className="text-sm font-bold text-blue-800">
                        ₹{available}
                    </span>
                </div>

                {/* Pending Amount */}
                <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                    <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-yellow-600" />
                        <span className="text-sm font-medium text-yellow-800">Pending</span>
                    </div>
                    <span className="text-sm font-bold text-yellow-800">₹{pending}</span>
                </div>

                {/* Withdraw Amount */}
                <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                    <div className="flex items-center gap-2">
                        <Download className="w-4 h-4 text-green-600" />
                        <span className="text-sm font-medium text-green-800">Withdrawn</span>
                    </div>
                    <span className="text-sm font-bold text-green-800">₹{withdraw}</span>
                </div>
            </div>
        </div>
    );

    const initialCall = async () => {
        try {
            const { status, result } = await getDashboard();
            if (status === true) {
                setBalanceInfo(result)
            }
        } catch (error) { }
    }

    useEffect(() => {
        initialCall()
    }, [])

    // console.log(balanceInfo, 'balanceInfobalanceInfobalanceInfo')
    return (
        <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 p-4 md:p-8">
            <div className="max-w-7xl mx-auto">
                {/* Welcome Header */}
                <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                        <div>
                            <h1 className="text-2xl md:text-3xl font-bold text-gray-800">
                                Welcome back, Gateway Promoters!
                            </h1>
                        </div>
                    </div>
                </div>

                {/* Stats Cards - Now 4 cards in 2x2 grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                    <IncomeCard
                        title="Binary Income"
                        total={balanceInfo.total_binary_bonus}
                        available={balanceInfo.available_binary_bonus}
                        pending={balanceInfo.pending_binary_bonus}
                        withdraw={balanceInfo.withdraw_binary_bonus}
                        icon={TrendingUp}
                        gradientFrom="from-blue-500"
                        gradientTo="to-blue-600"
                    />

                    <IncomeCard
                        title="Business Development Fund"
                        total={balanceInfo.total_business_bonus}
                        available={balanceInfo.available_business_bonus}
                        pending={balanceInfo.pending_business_bonus}
                        withdraw={balanceInfo.withdraw_business_bonus}
                        icon={Users}
                        gradientFrom="from-purple-500"
                        gradientTo="to-purple-600"
                    />

                    <IncomeCard
                        title="Cash Awards"
                        total={balanceInfo.total_cash_bonus}
                        available={balanceInfo.available_cash_bonus}
                        pending={balanceInfo.pending_cash_bonus}
                        withdraw={balanceInfo.withdraw_cash_bonus}
                        icon={Gift}
                        gradientFrom="from-pink-500"
                        gradientTo="to-pink-600"
                    />

                    <IncomeCard
                        title="Refund Bonus"
                        total={balanceInfo.total_refund_bonus}
                        available={balanceInfo.available_refund_bonus}
                        pending={balanceInfo.pending_refund_bonus}
                        withdraw={balanceInfo.withdraw_refund_bonus}
                        icon={RefreshCw}
                        gradientFrom="from-orange-500"
                        gradientTo="to-orange-600"
                    />
                </div>

                {/* Summary Section */}
                <div className="bg-white rounded-xl shadow-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">Income Summary</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="text-center p-4 bg-gray-50 rounded-lg">
                            <p className="text-sm text-gray-600 mb-1">Total Earnings</p>
                            <p className="text-xl font-bold text-gray-800">
                                ₹{(
                                    balanceInfo.total_binary_bonus +
                                    balanceInfo.total_business_bonus +
                                    balanceInfo.total_cash_bonus +
                                    balanceInfo.total_refund_bonus
                                )}
                            </p>
                        </div>
                        <div className="text-center p-4 bg-gray-50 rounded-lg">
                            <p className="text-sm text-gray-600 mb-1">Total Pending</p>
                            <p className="text-xl font-bold text-yellow-600">
                                ₹{(
                                    balanceInfo.pending_binary_bonus +
                                    balanceInfo.pending_business_bonus +
                                    balanceInfo.pending_cash_bonus +
                                    balanceInfo.pending_refund_bonus +
                                    balanceInfo.available_binary_bonus +
                                    balanceInfo.available_business_bonus +
                                    balanceInfo.available_cash_bonus +
                                    balanceInfo.available_refund_bonus
                                )}
                            </p>
                        </div>
                        <div className="text-center p-4 bg-gray-50 rounded-lg">
                            <p className="text-sm text-gray-600 mb-1">Total Withdrawn</p>
                            <p className="text-xl font-bold text-green-600">
                                ₹{(
                                    balanceInfo.withdraw_binary_bonus +
                                    balanceInfo.withdraw_business_bonus +
                                    balanceInfo.withdraw_cash_bonus +
                                    balanceInfo.withdraw_refund_bonus
                                )}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Dashboard;